import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { Button } from '@sohanscript/ui/button';
import type * as React from 'react';
import {
	SurfaceFrame,
	type SurfaceMaximizedMode,
	type SurfacePlacement,
	type SurfaceState,
	type SurfaceVariant,
} from './surface-frame';

export type { SurfaceDrag } from './surface-context';
export { useSurface } from './surface-context';
// NOTE: the surface primitives are re-exported here on purpose. A brand new
// subpath of this package (`@sohanscript/ui/<new>`) does not resolve inside
// the app program, so apps import them from the entry point they already
// resolve instead of from a fresh one.
export {
	SurfaceFrame,
	type SurfaceMaximizedMode,
	SurfaceResizeHandles,
	type SurfaceState,
	type SurfaceVariant,
	useSurfaceDrag,
} from './surface-frame';
export type { SurfacePlacement } from './surface-geometry';

export { WindowChrome, type WindowChromeProps } from './window-chrome';

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
	className,
	...props
}: DialogPrimitive.Backdrop.Props) {
	return (
		<DialogPrimitive.Backdrop
			data-slot="dialog-overlay"
			className={cn(
				'data-open:fade-in-0 data-closed:fade-out-0 fixed inset-0 isolate z-50 bg-black/10 duration-100 data-closed:animate-out data-open:animate-in supports-backdrop-filter:backdrop-blur-xs',
				className,
			)}
			{...props}
		/>
	);
}

function DialogContent({
	className,
	children,
	showCloseButton = true,
	surface = 'modal',
	placement,
	maximized = false,
	maximizedMode = 'cover',
	draggable = false,
	initialOffset,
	state = 'open',
	minVisible = 100,
	...props
}: DialogPrimitive.Popup.Props & {
	showCloseButton?: boolean;
	/** The frame the popup is rendered with, defaults to the modal skin. */
	surface?: SurfaceVariant;
	/** Where the frame sits before it is dragged. Defaults to `center` for modals, `origin` for windows. */
	placement?: SurfacePlacement;
	/** Renders the maximized geometry. */
	maximized?: boolean;
	/** What maximized means, defaults to `cover`. */
	maximizedMode?: SurfaceMaximizedMode;
	/** Allows dragging the popup from its chrome. */
	draggable?: boolean;
	/** Offset applied to the initial position. */
	initialOffset?: { x: number; y: number };
	/** Lifecycle of the surface, drives the shared transition. */
	state?: SurfaceState;
	/** Minimum visible pixels when dragging off-screen on left/right/bottom edges. Defaults to 100px. */
	minVisible?: number;
}) {
	// NOTE: a modal dims the page behind it, a window floats above it.
	const hasOverlay = surface === 'modal';

	// Draggable windows use origin placement so minVisible bounds apply;
	// modals stay centered unless explicitly overridden.
	const resolvedPlacement =
		placement ?? (draggable && surface === 'window' ? 'origin' : 'center');

	return (
		<DialogPortal>
			{hasOverlay && <DialogOverlay />}
			<DialogPrimitive.Popup
				data-slot="dialog-content"
				render={
					<SurfaceFrame
						variant={surface}
						placement={resolvedPlacement}
						maximized={maximized}
						maximizedMode={maximizedMode}
						draggable={draggable}
						initialOffset={initialOffset}
						state={state}
						minVisible={minVisible}
					/>
				}
				className={cn(
					// NOTE: the frame animates the surface in and out, only a
					// modal keeps the CSS exit so it can unmount on its own.
					hasOverlay &&
						'data-closed:fade-out-0 data-closed:zoom-out-95 duration-100 data-closed:animate-out',
					className,
				)}
				{...props}
			>
				{children}
				{showCloseButton && (
					<DialogPrimitive.Close
						data-slot="dialog-close"
						render={
							<Button
								variant="ghost"
								className="absolute top-2 right-2"
								size="icon-sm"
								icon="lucide:x"
							>
								<span className="sr-only">{m.ui_close()}</span>
							</Button>
						}
					/>
				)}
			</DialogPrimitive.Popup>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-header"
			className={cn('flex flex-col gap-2', className)}
			{...props}
		/>
	);
}

function DialogFooter({
	className,
	showCloseButton = false,
	children,
	...props
}: React.ComponentProps<'div'> & {
	showCloseButton?: boolean;
}) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				'corner-bevel -mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end',
				className,
			)}
			{...props}
		>
			{children}
			{showCloseButton && (
				<DialogPrimitive.Close
					render={<Button variant="outline">{m.ui_close()}</Button>}
				/>
			)}
		</div>
	);
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn(
				'cn-font-heading font-medium text-base leading-none',
				className,
			)}
			{...props}
		/>
	);
}

function DialogDescription({
	className,
	...props
}: DialogPrimitive.Description.Props) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn(
				'text-muted-foreground text-sm *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
				className,
			)}
			{...props}
		/>
	);
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
